import  { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Header } from '@/components/header';
import { Footer } from '@/components/footer';
import Cta from '@/components/cta';
import { FAQ } from '@/components/faq';
import { Partners } from '@/components/partners';
import { useTranslation } from "react-i18next";
import { t } from 'i18next';
import AdvantagesSlider from './AdvantagesSlider';
import { Button } from '@/components/ui/button';
import { Link } from 'wouter';
import { HowItWorks } from '@/components/HowItWorks';
import { ICONS } from '@/assets/icons';

type Feature = {
  icon: any;
  title: string;
  desc: string;
};

type UseCase = {
  icon: any;
  title: string;
  description: string;
  image: string;
};

const advantages: Feature[] = [
  {
    icon: ICONS.immediateAssistance, // Represents fall detection
    title: t("models.fallDetection.details.advantages.title1"),
    desc: t("models.fallDetection.details.advantages.description1")
  },
  {
    icon: ICONS.nonIntrusiveMonitoring, // Represents health monitoring
    title: t("models.fallDetection.details.advantages.title2"),
    desc: t("models.fallDetection.details.advantages.description2")
  },
  {
    icon: ICONS.twentyFourSevenMonitoring, // Represents alert/notification
    title: t("models.fallDetection.details.advantages.title3"),
    desc: t("models.fallDetection.details.advantages.description3")
  },
  {
    icon: ICONS.easyIntegration, // Represents protection/safety
    title: t("models.fallDetection.details.advantages.title4"),
    desc: t("models.fallDetection.details.advantages.description4")
  },
  {
    icon: ICONS.increaseSafety, // Health/response
    title: t("models.fallDetection.details.advantages.title5"),
    desc: t("models.fallDetection.details.advantages.description5")
  },
  {
    icon: ICONS.reducedResponseTime, // Alert
    title: t("models.fallDetection.details.advantages.title6"),
    desc: t("models.fallDetection.details.advantages.description6")
  },
  {
    icon: ICONS.proactivePrevention, // Alert
    title: t("models.fallDetection.details.advantages.title7"),
    desc: t("models.fallDetection.details.advantages.description7")
  }
];


const howItWorks = [
  {
    title: t("models.fallDetection.details.howItWorks.title1"),
    desc: t("models.fallDetection.details.howItWorks.description1"),
    icon: ICONS.analyseTempsReel // Step 1: fall event
  },
  {
    title: t("models.fallDetection.details.howItWorks.title2"),
    desc: t("models.fallDetection.details.howItWorks.description2"),
    icon: ICONS.motionRecognition // Step 2: health/monitoring
  },
  {
    title: t("models.fallDetection.details.howItWorks.title3"),
    desc: t("models.fallDetection.details.howItWorks.description3"),
    icon: ICONS.alertsInstantanees // Step 3: alert/notify
  },
];

    ({ service }: { service: Feature | UseCase }) => (
  <div className="group rounded-xl p-6 bg-white dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 transition-all duration-300 hover:bg-slate-900 hover:text-white hover:shadow-xl relative overflow-hidden">
    <div className="absolute top-0 left-0 w-full h-1 bg-slate-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
    <div className="w-12 h-12 rounded-full flex items-center justify-center text-2xl mb-4 bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-200 group-hover:bg-white/20 group-hover:text-white transition-all duration-300">
      <FontAwesomeIcon icon={service.icon} />
    </div>
    <h4 className="text-lg font-bold mb-2">{service.title}</h4>
    <p className="text-sm opacity-90">{'desc' in service ? service.desc : service.description}</p>
  </div>
);

const FallModelDetails = () => {
  const { t } = useTranslation();


  const [imgInView, setImgInView] = useState(false);
  const [stepInView, setStepInView] = useState([false, false, false]);

  useEffect(() => {
    setImgInView(true);
    const timers = howItWorks.map((_, i) =>
      setTimeout(() => setStepInView((prev) => {
        const newState = [...prev];
        newState[i] = true;
        return newState;
      }), i * 300)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <>
      <Header />
      {/* Hero Section */}
      <div id="hero" className="relative h-screen w-full overflow-hidden">
        {/* Background GIF with Overlay */}
        <div className="absolute inset-0 z-0">
          {/* Blue gradient overlays for cinematic darkening */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#001a33]/80 via-[#001a33]/60 to-transparent" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(0,0,0,0.35)_0%,_rgba(0,0,0,0.65)_100%)]" />
          <img
            src="./Fall.png"
            alt="Fall detection visual"
            className={`w-full h-auto object-cover rounded-xl shadow-2xl transition-all duration-700 ease-in-out ${imgInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
          />
        </div>

        {/* Content */}
        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="w-full px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto sm:mx-0 text-white px-6 sm:pl-6 sm:pr-0 md:pl-6 lg:pl-6 xl:pl-6 text-center sm:text-left">
              <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-korolev font-bold leading-tight tracking-wide">
                <span className="block pt-3 sm:pt-4 md:pt-5 text-primary-200/90 tracking-wider">
                {t('models.fallDetection.details.title')}
                </span>
              </h1>
              <h2 className="hidden sm:block mt-3 sm:mt-5 text-lg sm:text-2xl md:text-2xl font-korolev font-medium text-gray-100 max-w-2xl">
                {t('models.fallDetection.details.description')}
              </h2>


              <div className="mt-6 sm:mt-12 flex flex-col sm:flex-row gap-3 sm:gap-4 font-poppins">
                <Link href="/free-trial" className="group relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-primary/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <Button
                    size="lg"
                    className="w-full sm:w-auto bg-white text-black hover:bg-gray-50 hover:scale-[1.02] px-4 py-4 sm:px-6 sm:py-6 text-sm sm:text-lg font-medium transition-all duration-300 hover:shadow-lg group-hover:shadow-primary/20"
                  >
                    <span className="relative z-10">
                      {t('models.requestDemo')}
                    </span>
                  </Button>
                </Link>
                <Link
                  href="/contact-sales"
                  className="group relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full sm:w-auto bg-transparent border-white/70 text-white hover:bg-white/10 hover:border-white/90 hover:scale-[1.02] px-4 py-4 sm:px-6 sm:py-6 text-sm sm:text-lg font-medium transition-all duration-300 hover:shadow-lg group-hover:shadow-white/10"
                  >
                    <span className="relative z-10">{t('models.contactUs')}</span>
                  </Button>
                </Link>
              </div>

            </div>
          </div>
        </div>
        {/* Scroll Indicator */}
        <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 opacity-40">
          <button
            onClick={() => {
              window.scrollTo({
                top: window.innerHeight,
                behavior: 'smooth'
              });
            }}
            aria-label="Scroll down"
            className="flex flex-col items-center text-white hover:text-primary-200 transition-colors group"
          >
            <span className="text-sm font-medium mb-2 opacity-80 group-hover:opacity-100 transition-opacity">
              {t('hero.scrollDown', 'Scroll down')}
            </span>
            <div className="w-10 h-16 rounded-full border-2 border-white/50 flex items-start justify-center p-1 group-hover:border-white transition-colors">
              <div className="w-1 h-4 bg-white rounded-full animate-bounce" />
            </div>
          </button>
        </div>
      </div>
      {/* How It Works Section */}
      <HowItWorks
        title={t('models.fallDetection.details.howItWorks.title')}
        steps={howItWorks}
        stepInView={stepInView}
      />

      {/* Advantages and Limitations Section */}
      <section className="py-20 bg-gradient-to-b from-white to-slate-50 dark:from-slate-900 dark:to-slate-800 text-slate-900 dark:text-white px-4 md:px-8 lg:px-16 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-black/5 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-black/5 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-16 text-center dark:text-white text-slate-900">
            {t("models.fallDetection.details.advantages.title")}
          </h2>
          <div className="flex flex-col lg:flex-row gap-16 items-stretch w-full">
            {/* Advantages Slider */}
            <div className="flex-1 flex flex-col min-w-0 max-w-full h-full">
              <h3 className="text-2xl font-bold mb-4 text-left pl-4 border-l-4 border-slate-900 dark:border-white">
                {t("models.fireSmokeDetection.details.advantages.title")}
              </h3>
              <div className="flex-1 flex flex-col h-full min-h-[260px]">
                <AdvantagesSlider items={advantages}
                  itemClassName="group flex items-center gap-4 py-4 px-6 border-b border-slate-200 dark:border-slate-700 last:border-0 hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-all duration-300 min-h-[90px]"
                />
              </div>
            </div>

          </div>
        </div>
      </section>



      <Partners />
      <Cta />
      <div className="mt-20 mb-20 mx-5 md:mx-0">
      <FAQ faqs={t('models.fallDetection.details.faq.faq', { returnObjects: true }) as Array<{id?: string, question: string, answer: string}>} title={t('faq.title')} subtitle={t('models.fallDetection.faqDescription')}/>
      </div>
      <Footer />
    </>
  );
};

export default FallModelDetails;
